import { Button, Div, Text } from "atomize"
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen, faRotateLeft, faSyncAlt, faXmark } from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import { theme } from "../settings/config"
import { lessonsContext } from "../providers/lessons.provider"
import { schoolContext } from "../providers/school.provider"
import { profileContext } from "../providers/profile.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { modalContext } from "../providers/modal.provider"
import { canEditLesson, getLessonTypeTileColors } from "../utils/func.utils"
import {
	assignLanes,
	formatElsewhere,
	formatHours,
	formatMinutes,
	getBookedMinutes,
	getDayTitle,
	getGridRange,
	getLessonPlayersText,
	getWeekDays,
	isElsewhere,
	lessonEndMinutes,
	lessonStartMinutes,
	withLessonsElsewhere,
	workedLessons,
} from "../utils/week.utils"
import LessonsSkeleton from "../atoms/LessonsSkeleton"
import LessonTypeLabel from "../atoms/LessonTypeLabel"
import WeekCourtPill from "./WeekCourtPill"
import { isCancelledLesson } from "../utils/cancellation.utils"
import {
	ELSEWHERE_OUTLINE_OPACITY,
	ELSEWHERE_TINT_OPACITY,
	useElsewhereLessonType,
	useGoToElsewhereLesson,
	useSchoolName,
} from "./ElsewhereLessonRow"
import DashedOutline from "../atoms/DashedOutline"
import SchoolAvatar from "../school/SchoolAvatar"
import { getLessonTypeLabel } from "../utils/func.utils"

// ──────────────────────────────────────────────────────────────────────────
// LA SETTIMANA SUL COMPUTER
//
// Una griglia da calendario: le ore in verticale, un giorno per colonna, e
// ogni lezione un blocco alto quanto dura, nel colore della sua tipologia.
// I buchi sono lo spazio vuoto tra un blocco e l'altro.
//
// Il colore è quello del tassello e non quello della scheda: la privata in
// elenco è bianca, e un blocco bianco su una griglia bianca non si vede.
//
// Toccando una lezione si apre un riquadro lì accanto, e si resta sulla
// settimana; toccando il nome di un giorno si va a quel giorno.
//
// Le lezioni negli altri circoli, se si è scelto di vederle, sono blocchi a
// righe e senza colore: occupano il loro posto nella giornata, e il loro
// riquadro porta al circolo dove si modificano.
// ──────────────────────────────────────────────────────────────────────────

// Quanto è alta un'ora. La griglia si allunga fino alla barra in basso, così
// la settimana sta in una schermata sola: non si scorre e sotto non resta
// una fascia vuota. Su uno schermo basso le ore non scendono sotto una
// misura che si legge ancora, e allora si scorre.
const MIN_HOUR_HEIGHT = 32
const MAX_HOUR_HEIGHT = 90
// lo spazio sotto la griglia che tiene la barra, lo stesso che la pagina
// delle lezioni lascia sotto l'ultima riga
export const WEEK_GRID_BOTTOM_SPACE = 104
// sotto questa altezza un blocco non ha posto per tre righe: orario e
// tipologia vanno sulla stessa
const COMPACT_BLOCK_HEIGHT = 44

const useFittedHourHeight = (bodyRef, hoursCount, isReady) => {
	const [hourHeight, setHourHeight] = useState(MIN_HOUR_HEIGHT)

	useLayoutEffect(() => {
		if (!isReady) return
		const fit = () => {
			const top = bodyRef.current?.getBoundingClientRect().top
			if (top === undefined) return
			const available = window.innerHeight - top - WEEK_GRID_BOTTOM_SPACE - 16
			setHourHeight(
				Math.min(MAX_HOUR_HEIGHT, Math.max(MIN_HOUR_HEIGHT, available / hoursCount)),
			)
		}
		fit()
		window.addEventListener("resize", fit)
		return () => window.removeEventListener("resize", fit)
	}, [bodyRef, hoursCount, isReady])

	return hourHeight
}

function WeekGrid() {
	const {
		selectedDate,
		weekLessons,
		elsewhereLessons,
		setSelectedDate,
		setLessonsView,
		setLessonToOpen,
	} = useContext(lessonsContext)
	const { selectedSchool } = useContext(schoolContext)
	const [popover, setPopover] = useState(null)
	const bodyRef = useRef(null)

	const days = getWeekDays(selectedDate)
	const monday = days[0]

	// le lezioni del circolo e quelle altrove, giorno per giorno
	const shownByDate = Object.fromEntries(
		days.map(date => [
			date,
			withLessonsElsewhere(weekLessons?.[date], elsewhereLessons[date]),
		]),
	)

	const { from, to } = getGridRange(shownByDate)
	const hourHeight = useFittedHourHeight(bodyRef, (to - from) / 60, Boolean(weekLessons))

	// cambiando settimana o circolo il riquadro di una lezione che non c'è
	// più non resta per aria
	useEffect(() => setPopover(null), [monday, selectedSchool])

	useEffect(() => {
		if (!popover) return
		const onKey = event => event.key === "Escape" && setPopover(null)
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [popover])

	const goToDay = (date, lesson) => {
		setLessonToOpen(lesson ? `${lesson.coachID}_${lesson.lessonID}` : null)
		setSelectedDate(date)
		setLessonsView("day")
	}

	if (!weekLessons) return <LessonsSkeleton />

	const height = ((to - from) / 60) * hourHeight
	// le ore intere dentro la fascia, esclusa la prima: la sua riga è il bordo
	const hours = []
	for (let minutes = from + 60; minutes < to; minutes += 60) hours.push(minutes)

	return (
		<motion.div
			key={`${selectedSchool}_${days[0]}`}
			animate={{ y: [20, 0] }}
			transition={{ ease: [0.04, 0.62, 0.23, 0.98], duration: 0.4 }}>
			<Div
				m={{ t: "1rem" }}
				p={{ t: "0.75rem", r: "0.75rem", b: "1rem" }}
				rounded="20px"
				bg={theme.colors.surface}
				shadow={theme.shadows.accent}
				style={{ display: "grid", gridTemplateColumns: "3.5rem repeat(7, minmax(0, 1fr))" }}>
				<div />
				{days.map(date => (
					<WeekGridHeader
						key={date}
						date={date}
						lessons={weekLessons[date] ?? []}
						elsewhereCount={elsewhereLessons[date]?.length ?? 0}
						onClick={() => goToDay(date)}
					/>
				))}

				<Div ref={bodyRef} pos="relative" h={`${height}px`}>
					{hours.map(minutes => (
						<Text
							key={minutes}
							pos="absolute"
							right="0.6rem"
							top={`${((minutes - from) / 60) * hourHeight}px`}
							textSize="caption"
							textColor={theme.colors.text_light}
							style={{ transform: "translateY(-50%)", fontVariantNumeric: "tabular-nums" }}>
							{formatMinutes(minutes)}
						</Text>
					))}
				</Div>

				{days.map(date => (
					<Div
						key={date}
						pos="relative"
						h={`${height}px`}
						bg={getDayTitle(date).isToday ? theme.colors.gray_blur : "transparent"}
						style={{
							borderLeft: `1px solid ${theme.colors.gray_light}`,
							backgroundImage: `linear-gradient(${theme.colors.gray_light} 1px, transparent 1px)`,
							backgroundSize: `100% ${hourHeight}px`,
						}}>
						{assignLanes(shownByDate[date]).map(({ lesson, lane, lanes }) => (
							<WeekGridBlock
								key={lesson.lessonID}
								lesson={lesson}
								from={from}
								hourHeight={hourHeight}
								lane={lane}
								lanes={lanes}
								onClick={event =>
									setPopover({
										lesson,
										date,
										rect: event.currentTarget.getBoundingClientRect(),
									})
								}
							/>
						))}
					</Div>
				))}
			</Div>

			{popover &&
				(isElsewhere(popover.lesson, selectedSchool) ? (
					<ElsewhereLessonPopover {...popover} onClose={() => setPopover(null)} />
				) : (
					<WeekLessonPopover
						{...popover}
						onClose={() => setPopover(null)}
						onGoToDay={() => goToDay(popover.date, popover.lesson)}
					/>
				))}
		</motion.div>
	)
}

function WeekGridHeader({ date, lessons, elsewhereCount, onClick }) {
	const { isToday } = getDayTitle(date)
	const day = DateTime.fromISO(date).setLocale("it")
	// "Gioco io" sta in agenda, ma non è una lezione né ore di lavoro
	const worked = workedLessons(lessons)

	return (
		<Div
			textAlign="center"
			p={{ b: "0.5rem" }}
			rounded="15px"
			cursor="pointer"
			hoverBg={theme.colors.primary_light}
			transition
			onClick={onClick}>
			<Text textSize="caption" textColor={theme.colors.text_light}>
				{day.toFormat("ccc")}
			</Text>
			<Text
				textSize="subheader"
				textWeight="bold"
				m="auto"
				w="2rem"
				h="2rem"
				d="flex"
				justify="center"
				align="center"
				rounded="circle"
				bg={isToday ? theme.colors.primary : "transparent"}
				textColor={isToday ? theme.colors.white : theme.colors.gunmetal}>
				{day.day}
			</Text>
			<Text
				textSize="tiny"
				textColor={theme.colors.text_light}
				style={{ fontVariantNumeric: "tabular-nums" }}>
				{worked.length > 0
					? `${worked.length} lez. · ${formatHours(getBookedMinutes(worked))}`
					: "—"}
			</Text>
			{elsewhereCount > 0 && (
				<Text
					textSize="tiny"
					textColor={theme.colors.gunmetal_light}
					style={{ fontVariantNumeric: "tabular-nums" }}>
					{formatElsewhere(elsewhereCount)}
				</Text>
			)}
		</Div>
	)
}

function WeekGridBlock(props) {
	const { selectedSchool } = useContext(schoolContext)
	return isElsewhere(props.lesson, selectedSchool) ? (
		<ElsewhereGridBlock {...props} />
	) : (
		<SchoolGridBlock {...props} />
	)
}

// Dove sta un blocco nella colonna del suo giorno: alto quanto dura, largo
// quanto la sua corsia
const blockBox = (lesson, from, hourHeight, lane, lanes) => {
	const start = lessonStartMinutes(lesson)
	const end = lessonEndMinutes(lesson)
	const top = ((start - from) / 60) * hourHeight
	return {
		top: `${top + 1}px`,
		height: Math.max(((end - start) / 60) * hourHeight - 2, 18),
		left: `calc(${(lane / lanes) * 100}% + 3px)`,
		width: `calc(${100 / lanes}% - 6px)`,
	}
}

// Una lezione in un altro circolo: c'è, occupa il suo tempo, ma non è di
// qui. Ha il colore della sua tipologia, attenuato e a tratteggio.
function ElsewhereGridBlock({ lesson, from, hourHeight, lane, lanes, onClick }) {
	const lessonTypeOf = useElsewhereLessonType()
	const schoolName = useSchoolName()
	const box = blockBox(lesson, from, hourHeight, lane, lanes)
	const school = schoolName(lesson.schoolID)
	const lessonType = lessonTypeOf(lesson)
	const colors = getLessonTypeTileColors(lessonType)
	const time = `${formatMinutes(lessonStartMinutes(lesson))}–${formatMinutes(lessonEndMinutes(lesson))}`

	return (
		<Div
			tag="button"
			type="button"
			aria-label={`${time}, lezione al ${school}`}
			pos="absolute"
			top={box.top}
			h={`${box.height}px`}
			p="0.2rem 0.35rem"
			rounded="10px"
			border="0"
			cursor="pointer"
			textAlign="left"
			d="flex"
			flexDir="column"
			style={{
				left: box.left,
				width: box.width,
				overflow: "hidden",
				backgroundColor: "transparent",
				isolation: "isolate",
				font: "inherit",
			}}
			onClick={onClick}>
			<Div
				pos="absolute"
				rounded="10px"
				bg={colors.bg}
				style={{ inset: 0, zIndex: -1, opacity: ELSEWHERE_TINT_OPACITY }}
			/>
			<DashedOutline color={colors.text} radius={10} opacity={ELSEWHERE_OUTLINE_OPACITY} />
			<Text
				textSize="tiny"
				textWeight="bold"
				textColor={theme.colors.gunmetal}
				style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
				{time}
			</Text>
			<Text
				textSize="tiny"
				textWeight="600"
				textColor={theme.colors.gunmetal}
				style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
				{school}
			</Text>
			{box.height >= COMPACT_BLOCK_HEIGHT && (
				<Text
					textSize="tiny"
					textColor={colors.text}
					style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
					{getLessonTypeLabel(lesson.lessonType, lessonType)}
				</Text>
			)}
		</Div>
	)
}

function SchoolGridBlock({ lesson, from, hourHeight, lane, lanes, onClick }) {
	const { getLessonType } = useContext(sharedDataContext)
	const lessonType = getLessonType(lesson.lessonType)
	const colors = getLessonTypeTileColors(lessonType)

	const start = lessonStartMinutes(lesson)
	const end = lessonEndMinutes(lesson)
	const top = ((start - from) / 60) * hourHeight
	const height = Math.max(((end - start) / 60) * hourHeight - 2, 18)
	const players = getLessonPlayersText(lesson)
	const isCompact = height < COMPACT_BLOCK_HEIGHT
	const isCancelled = isCancelledLesson(lesson)
	const typeLabel = (
		<>
			<LessonTypeLabel
				lessonTypeID={lesson.lessonType}
				lessonType={lessonType}
				background={colors.bg}
			/>
			{isCancelled && " · Annullata"}
		</>
	)

	return (
		<Div
			pos="absolute"
			top={`${top + 1}px`}
			h={`${height}px`}
			p="0.25rem 0.4rem"
			rounded="10px"
			bg={colors.bg}
			cursor="pointer"
			style={{
				left: `calc(${(lane / lanes) * 100}% + 3px)`,
				width: `calc(${100 / lanes}% - 6px)`,
				overflow: "hidden",
				// annullata: c'è ancora, ma non si fa
				...(isCancelled && { opacity: 0.5, filter: "grayscale(1)" }),
			}}
			onClick={onClick}>
			<Div d="flex" justify="space-between" align="start" style={{ gap: "0.25rem" }}>
				<Text
					textSize="tiny"
					textWeight="bold"
					textColor={colors.text}
					style={{
						fontVariantNumeric: "tabular-nums",
						whiteSpace: "nowrap",
						...(isCancelled && { textDecoration: "line-through" }),
					}}>
					{formatMinutes(start)}–{formatMinutes(end)}
					{isCompact && (
						<Text tag="span" textWeight="normal">
							{" · "}
							{typeLabel}
						</Text>
					)}
				</Text>
				{lanes === 1 && <WeekCourtPill lesson={lesson} compact />}
			</Div>
			{!isCompact && (
				<Text textSize="tiny" textColor={colors.text} style={{ whiteSpace: "nowrap" }}>
					{typeLabel}
				</Text>
			)}
			{players && (
				<Text
					textSize="tiny"
					textColor={colors.text}
					style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
					{players}
				</Text>
			)}
		</Div>
	)
}

const POPOVER_WIDTH = 300

// di fianco alla lezione, dal lato dove c'è posto
const popoverPosition = rect => {
	const fitsRight = rect.right + POPOVER_WIDTH + 16 < window.innerWidth
	const left = fitsRight ? rect.right + 8 : rect.left - POPOVER_WIDTH - 8
	return {
		left: Math.max(16, left),
		top: Math.min(Math.max(rect.top, 16), window.innerHeight - 280),
	}
}

function WeekLessonPopover({ lesson, date, rect, onClose, onGoToDay }) {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { selectedSchool } = useContext(schoolContext)
	const { getLessonType } = useContext(sharedDataContext)

	const lessonType = getLessonType(lesson.lessonType)
	const colors = getLessonTypeTileColors(lessonType)
	const canEdit = canEditLesson(coach, selectedSchool, lesson)
	const isCancelled = isCancelledLesson(lesson)
	const day = DateTime.fromISO(date).setLocale("it").toFormat("cccc d LLLL")

	const { left, top } = popoverPosition(rect)

	return (
		<>
			{/* un tocco fuori chiude, come ci si aspetta da un riquadro così */}
			<Div pos="fixed" top="0" left="0" w="100vw" h="100vh" zIndex="3" onClick={onClose} />
			<motion.div
				initial={{ opacity: 0, y: 6 }}
				animate={{ opacity: 1, y: 0 }}
				style={{
					position: "fixed",
					left,
					top,
					width: POPOVER_WIDTH,
					zIndex: 4,
				}}>
				<Div
					p="1rem"
					rounded="20px"
					bg={theme.colors.surface}
					shadow={theme.shadows["top-3"]}
					d="flex"
					flexDir="column"
					style={{ gap: "0.6rem" }}>
					<Div d="flex" justify="space-between" align="center">
						<Text
							textSize="caption"
							textWeight="bold"
							p="0.1rem 0.6rem"
							rounded="10px"
							bg={colors.bg}
							textColor={colors.text}>
							<LessonTypeLabel
								lessonTypeID={lesson.lessonType}
								lessonType={lessonType}
								background={colors.bg}
							/>
							{isCancelled && " · Annullata"}
						</Text>
						<Div cursor="pointer" p="0.25rem" onClick={onClose} title="Chiudi">
							<FontAwesomeIcon icon={faXmark} color={theme.colors.text_light} />
						</Div>
					</Div>
					<Text textWeight="bold" textColor={theme.colors.gunmetal}>
						{day.charAt(0).toUpperCase() + day.slice(1)} ·{" "}
						{formatMinutes(lessonStartMinutes(lesson))}–
						{formatMinutes(lessonEndMinutes(lesson))}
						{lesson.recurrencyID && (
							<>
								{" · "}
								<FontAwesomeIcon icon={faSyncAlt} size="sm" />
							</>
						)}
					</Text>
					{getLessonPlayersText(lesson) && (
						<Text textSize="caption">{getLessonPlayersText(lesson)}</Text>
					)}
					<Div d="flex">
						<WeekCourtPill lesson={lesson} />
					</Div>
					<Div d="flex" justify="end" style={{ gap: "0.5rem" }} m={{ t: "0.25rem" }}>
						{canEdit && (
							<Button
								h="2.25rem"
								p={{ x: "0.9rem" }}
								rounded="15px"
								bg={theme.colors.gunmetal_lighter}
								textColor={theme.colors.gunmetal}
								style={{ gap: "0.35rem" }}
								onClick={() => {
									onClose()
									Modal.open(isCancelled ? "restoreLesson" : "editLesson", lesson)
								}}>
								<FontAwesomeIcon icon={isCancelled ? faRotateLeft : faPen} />
								{isCancelled ? "Ripristina" : "Modifica"}
							</Button>
						)}
						<Button
							h="2.25rem"
							p={{ x: "0.9rem" }}
							rounded="15px"
							bg={theme.colors.primary}
							textColor={theme.colors.white}
							textWeight="bold"
							onClick={onGoToDay}>
							Vai al giorno
						</Button>
					</Div>
				</Div>
			</motion.div>
		</>
	)
}

// Il riquadro di una lezione in un altro circolo: dov'è e quando, e la
// strada per andarci. Qui non si modifica niente.
function ElsewhereLessonPopover({ lesson, date, rect, onClose }) {
	const { schools } = useContext(schoolContext)
	const lessonTypeOf = useElsewhereLessonType()
	const schoolName = useSchoolName()
	const goToLesson = useGoToElsewhereLesson()

	const school = schoolName(lesson.schoolID)
	const day = DateTime.fromISO(date).setLocale("it").toFormat("cccc d LLLL")
	const { left, top } = popoverPosition(rect)

	return (
		<>
			<Div pos="fixed" top="0" left="0" w="100vw" h="100vh" zIndex="3" onClick={onClose} />
			<motion.div
				initial={{ opacity: 0, y: 6 }}
				animate={{ opacity: 1, y: 0 }}
				style={{ position: "fixed", left, top, width: POPOVER_WIDTH, zIndex: 4 }}>
				<Div
					p="1rem"
					rounded="20px"
					bg={theme.colors.surface}
					shadow={theme.shadows["top-3"]}
					d="flex"
					flexDir="column"
					style={{ gap: "0.6rem" }}>
					<Div d="flex" justify="space-between" align="center" style={{ gap: "0.5rem" }}>
						<Text
							textSize="caption"
							textWeight="bold"
							p="0.1rem 0.6rem"
							rounded="10px"
							bg={theme.colors.gunmetal_lighter}
							textColor={theme.colors.gunmetal}
							d="flex"
							align="center"
							style={{ gap: "0.35rem", minWidth: 0 }}>
							<SchoolAvatar school={schools[lesson.schoolID]} size={20} tone="onTint" />
							<span
								style={{
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}>
								{school}
							</span>
						</Text>
						<Div cursor="pointer" p="0.25rem" onClick={onClose} title="Chiudi">
							<FontAwesomeIcon icon={faXmark} color={theme.colors.text_light} />
						</Div>
					</Div>
					<Text textWeight="bold" textColor={theme.colors.gunmetal}>
						{day.charAt(0).toUpperCase() + day.slice(1)} ·{" "}
						{formatMinutes(lessonStartMinutes(lesson))}–
						{formatMinutes(lessonEndMinutes(lesson))}
					</Text>
					<Text textSize="caption">
						{getLessonTypeLabel(lesson.lessonType, lessonTypeOf(lesson))}
					</Text>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						Una tua lezione in un altro circolo: qui la vedi e basta, si modifica da lì.
					</Text>
					<Div d="flex" justify="end" m={{ t: "0.25rem" }}>
						<Button
							h="2.25rem"
							p={{ x: "0.9rem" }}
							rounded="15px"
							bg={theme.colors.primary}
							textColor={theme.colors.white}
							textWeight="bold"
							onClick={() => {
								onClose()
								goToLesson(lesson)
							}}>
							Apri nel circolo
						</Button>
					</Div>
				</Div>
			</motion.div>
		</>
	)
}

export default WeekGrid
